// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Pipeline persistence hook - bridges ReactFlow state with backend storage.
 *
 * Uses React Query for data fetching with automatic caching and deduplication.
 * Handles bidirectional transformation between ReactFlow nodes/edges and
 * WorkflowDefinition format.
 */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import type { Edge, Node } from '@xyflow/react'
import { toast } from 'sonner'
import type { PipelineNodeData } from '../../../core/contracts'
import { getPipelineNode } from '../../../core/pipeline-registry'
import type { Pipeline, WorkflowDefinition, WorkflowNode } from '../types'
import {
  mergeWorkflowNodeData,
  splitNodeDataForPersistence,
} from '../utils/workflow-node-helpers'
import { getServices } from '@/app-shell/services'
import {
  useCreatePipeline,
  useUpdatePipeline,
  useClonePipeline,
  useDeletePipeline,
  useSaveAgentPipeline,
} from './usePipelineQueries'

interface UsePipelinePersistenceArgs {
  organizationId?: string
  workspaceId: string
  /** Owning agent — pipeline and node writes go to the agent's partition. */
  agentId?: string
  nodes: Node<PipelineNodeData>[]
  edges: Edge[]
  setNodes: (value: Node<PipelineNodeData>[] | ((prev: Node<PipelineNodeData>[]) => Node<PipelineNodeData>[])) => void
  setEdges: (value: Edge[] | ((prev: Edge[]) => Edge[])) => void
  setSelectedNodeId: (value: string | null) => void
  onAfterPipelineLoad?: () => void
  /** Called after loading to arrange nodes nicely */
  onArrange?: () => void
  /** Pipelines from workspace config */
  pipelines?: Record<string, Pipeline> | null
}

/** Tracks node identity for detecting deletions */
interface SavedNodeIdentity {
  nodeDefinition: string
  instanceId: string
}

export const usePipelinePersistence = ({
  organizationId,
  workspaceId,
  agentId,
  nodes,
  edges,
  setNodes,
  setEdges,
  setSelectedNodeId,
  onAfterPipelineLoad,
  onArrange,
  pipelines: pipelinesProp,
}: UsePipelinePersistenceArgs) => {
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null)
  // Track node identities from last load/save to detect deletions
  const savedNodeIdentitiesRef = useRef<SavedNodeIdentity[]>([])

  // Just-created/cloned pipelines, kept until the workspace-config refetch
  // includes them. Without this overlay the self-heal effect below would see
  // the new active id missing from `pipelines` and snap back to the old one.
  const [pendingPipelines, setPendingPipelines] = useState<Record<string, Pipeline>>({})

  // Pipelines come from workspace config (no separate fetch needed); the
  // refetched prop wins over the overlay once it arrives.
  const pipelines = useMemo(
    () => ({ ...pendingPipelines, ...(pipelinesProp ?? {}) }),
    [pendingPipelines, pipelinesProp],
  )
  const loadingPipelines = false

  // Active pipeline is a binding when it inherits from a parent
  const isActiveBinding = activePipelineId ? Boolean(pipelines[activePipelineId]?.inherited) : false

  const createMutation = useCreatePipeline({ organizationId, workspaceId })
  const updateMutation = useUpdatePipeline({ organizationId, workspaceId })
  const cloneMutation = useClonePipeline({ organizationId, workspaceId })
  const deleteMutation = useDeletePipeline({ organizationId, workspaceId })
  const saveAgentMutation = useSaveAgentPipeline({ organizationId, workspaceId, agentId })

  /** Persist pipeline updates — full replace via the agent route, partial via the workspace route. */
  const persistPipeline = useCallback((pipelineId: string, updates: Partial<Pipeline>) => {
    if (agentId) {
      const base = pipelines[pipelineId]
      if (!base || !base.definition) {
        toast.error('Pipeline not loaded')
        return
      }
      const merged = { ...base, ...updates }
      saveAgentMutation.mutate({
        id: merged.id,
        name: merged.name,
        description: merged.description,
        hidden: merged.hidden,
        definition: merged.definition,
      })
    } else {
      updateMutation.mutate({ pipelineId, updates })
    }
  }, [agentId, pipelines, saveAgentMutation, updateMutation])

  /** Convert WorkflowDefinition to ReactFlow nodes/edges and load into canvas */
  const transformDefinitionToFlow = useCallback((
    definition?: WorkflowDefinition | null,
    origin?: { isInherited: boolean; workspaceNodeIds: string[] } | null,
  ) => {
    if (!definition || !Array.isArray(definition.nodes)) {
      setNodes([])
      setEdges([])
      setSelectedNodeId(null)
      savedNodeIdentitiesRef.current = []
      return
    }

    // Track node identities for detecting deletions on save
    savedNodeIdentitiesRef.current = definition.nodes
      .filter(node => node.nodeDefinition && node.instanceId)
      .map(node => ({
        nodeDefinition: node.nodeDefinition || node.type || 'unknown',
        instanceId: node.instanceId!,
      }))

    const workspaceIdSet = new Set(origin?.workspaceNodeIds ?? [])
    const isInherited = Boolean(origin?.isInherited)

    const flowNodes: Node<PipelineNodeData>[] = definition.nodes.map(node => {
      const nodeId = node.nodeDefinition || node.type || 'unknown'
      const nodeDef = getPipelineNode(nodeId)
      const mergedData = mergeWorkflowNodeData(node)
      const instanceId = typeof node.instanceId === 'string' ? node.instanceId.trim() : ''

      const label = typeof mergedData.label === 'string'
        ? mergedData.label
        : nodeDef?.display.label ?? nodeId

      const nodeDefinition = typeof mergedData.nodeDefinition === 'string'
        ? mergedData.nodeDefinition
        : nodeId

      const configured = typeof mergedData.configured === 'boolean'
        ? mergedData.configured
        : false

      // Backend stores nulls, but React/TypeScript prefer undefined for optional fields
      const cleanedData = Object.fromEntries(
        Object.entries(mergedData).map(([key, value]) => [key, value === null ? undefined : value])
      )

      const data: PipelineNodeData = { ...cleanedData, label, configured, nodeDefinition }

      // Set instanceId if present (identity field, not a setting)
      if (instanceId.length > 0) {
        (data as Record<string, unknown>)['instanceId'] = instanceId
      }

      // Origin from server-resolved Pipeline: nodes not in workspaceNodeIds came
      // from the parent (fromApp); nodes in the set are workspace-contributed
      // (isExtension when the pipeline itself is inherited).
      if (isInherited) {
        if (workspaceIdSet.has(node.id)) {
          (data as Record<string, unknown>).isExtension = true
        } else {
          (data as Record<string, unknown>).fromApp = true
        }
      }

      return {
        id: node.id,
        type: nodeId,
        // Position is recomputed by auto-arrange after load — seed with origin.
        position: { x: 0, y: 0 },
        data,
      }
    })

    const flowEdges: Edge[] = Array.isArray(definition.edges)
      ? definition.edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
        }))
      : []

    // Clear selection BEFORE updating nodes to prevent stale node references
    // (both pipelines may have nodes with the same id but different instanceIds)
    setSelectedNodeId(null)
    onAfterPipelineLoad?.()
    setNodes(flowNodes)
    setEdges(flowEdges)
  }, [onAfterPipelineLoad, setEdges, setNodes, setSelectedNodeId])

  const loadPipelineToCanvas = useCallback((pipeline: Pipeline | null, _pipelineId?: string) => {
    // Only load pipeline structure (nodes/edges layout) here.
    // Node settings are fetched lazily by ConfigurationPanel when a node is opened.
    const origin = pipeline
      ? {
          isInherited: Boolean(pipeline.inherited),
          workspaceNodeIds: pipeline.workspaceNodeIds ?? [],
        }
      : null
    transformDefinitionToFlow(pipeline?.definition, origin)
  }, [transformDefinitionToFlow])

  /** Convert ReactFlow nodes/edges to WorkflowDefinition for persistence */
  const buildDefinitionFromFlow = useCallback((
    nodesToSave?: Node<PipelineNodeData>[],
    edgesToSave?: Edge[],
  ): WorkflowDefinition => {
    const currentNodes = nodesToSave ?? nodes
    const currentEdges = edgesToSave ?? edges

    // Pipeline definition only stores layout (positions, edges) and node identity.
    // Node settings are stored separately in DynamoDB via putNodeInstance.
    const nodesForDefinition = currentNodes.map(node => {
      const data: PipelineNodeData = node.data ?? {
        label: node.type ?? node.id,
        configured: false,
        nodeDefinition: node.type ?? 'unknown',
      }
      const { topLevel, uiMetadata } = splitNodeDataForPersistence(data)

      return {
        id: node.id,
        type: node.type || 'unknown',
        nodeDefinition: topLevel.nodeDefinition || node.type || 'unknown',
        instanceId: topLevel.instanceId,
        uiMetadata: Object.keys(uiMetadata).length ? uiMetadata : undefined,
        // Note: config is NOT stored here - it's persisted separately via putNodeInstance
      } satisfies WorkflowNode
    })

    return {
      nodes: nodesForDefinition,
      edges: currentEdges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle || undefined,
        targetHandle: edge.targetHandle || undefined,
      })),
    }
  }, [edges, nodes])

  // CRUD operation handlers
  const handleSave = useCallback(async (
    nodesToSave?: Node<PipelineNodeData>[],
    edgesToSave?: Edge[],
  ) => {
    if (!activePipelineId) {
      toast.error('Cannot save pipeline: no active pipeline')
      return
    }
    if (!organizationId) {
      toast.error('Cannot save pipeline: no organization context')
      return
    }

    const currentNodes = nodesToSave ?? nodes
    const definition = buildDefinitionFromFlow(currentNodes, edgesToSave)

    // Save pipeline layout
    persistPipeline(activePipelineId, { definition })

    // Also persist each node's settings to DynamoDB
    const services = getServices()

    // Build set of current node identities for comparison
    const currentNodeIdentities = new Set(
      currentNodes
        .filter(node => node.data?.nodeDefinition && node.data?.instanceId)
        .map(node => `${node.data.nodeDefinition}::${node.data.instanceId}`)
    )

    // Find nodes that were removed (in saved but not in current)
    const removedNodes = savedNodeIdentitiesRef.current.filter(
      saved => !currentNodeIdentities.has(`${saved.nodeDefinition}::${saved.instanceId}`)
    )

    // Delete removed nodes from DynamoDB
    const deletePromises = removedNodes.map(async removed => {
      try {
        await services.nodeConfig.deleteNodeInstance(
          organizationId,
          workspaceId,
          activePipelineId,
          removed.instanceId,
          { agentId }
        )
      } catch (error) {
        console.error(`[usePipelinePersistence] Failed to delete node ${removed.instanceId}:`, error)
      }
    })

    // Only save node instances that are NEW (not previously loaded)
    const savedIdentitySet = new Set(
      savedNodeIdentitiesRef.current.map(s => `${s.nodeDefinition}::${s.instanceId}`)
    )
    const newNodes = currentNodes.filter(node => {
      const data = node.data
      if (!data?.nodeDefinition || !data?.instanceId) return false
      return !savedIdentitySet.has(`${data.nodeDefinition}::${data.instanceId}`)
    })

    const savePromises = newNodes.map(async node => {
      const data = node.data
      try {
        await services.nodeInstance.putNodeInstance(
          organizationId,
          workspaceId,
          activePipelineId,
          data.instanceId!,
          {
            label: typeof data.label === 'string' ? data.label : undefined,
            enabled: true,
            settings: { node_definition: data.nodeDefinition },
          },
          { agentId }
        )
      } catch (error) {
        console.error(`[usePipelinePersistence] Failed to save node ${data.instanceId}:`, error)
      }
    })

    await Promise.all([...deletePromises, ...savePromises])

    // Update saved identities to reflect current state
    savedNodeIdentitiesRef.current = currentNodes
      .filter(node => node.data?.nodeDefinition && node.data?.instanceId)
      .map(node => ({
        nodeDefinition: node.data.nodeDefinition,
        instanceId: node.data.instanceId!,
      }))
  }, [activePipelineId, agentId, buildDefinitionFromFlow, nodes, organizationId, persistPipeline, workspaceId])

  const handleEditPipeline = useCallback((pipelineId: string, updates: { name: string; description?: string }) => {
    if (isActiveBinding) {
      toast.error('Cannot edit linked pipeline: it is read-only')
      return
    }
    persistPipeline(pipelineId, updates)
  }, [isActiveBinding, persistPipeline])

  const handleTogglePipelineHidden = useCallback((pipelineId: string) => {
    const pipeline = pipelines[pipelineId]
    if (!pipeline) return
    persistPipeline(pipelineId, { hidden: !pipeline.hidden })
  }, [pipelines, persistPipeline])

  const handlePipelineSelect = useCallback((pipelineId: string) => {
    const pipeline = pipelines[pipelineId]
    if (!pipeline) {
      toast.error('Pipeline not found')
      return
    }
    setActivePipelineId(pipelineId)
    loadPipelineToCanvas(pipeline, pipelineId)
    // Auto-arrange after loading for nice layout
    requestAnimationFrame(() => onArrange?.())
  }, [loadPipelineToCanvas, onArrange, pipelines])

  const handleClonePipeline = useCallback(
    (pipelineId: string, data: { newPipelineId: string; newName: string }) => {
      if (agentId) {
        toast.error('Agent pipelines cannot be cloned here')
        return
      }
      const sourcePipeline = pipelines[pipelineId]
      if (!sourcePipeline) {
        toast.error('Pipeline not found')
        return
      }
      cloneMutation.mutate(
        {
          sourcePipelineId: pipelineId,
          newPipelineId: data.newPipelineId,
          newName: data.newName,
        },
        {
          onSuccess: (cloned) => {
            setPendingPipelines(prev => ({ ...prev, [cloned.id]: cloned }))
            setActivePipelineId(cloned.id)
            loadPipelineToCanvas(cloned, cloned.id)
            requestAnimationFrame(() => onArrange?.())
          },
        },
      )
    },
    [agentId, cloneMutation, loadPipelineToCanvas, onArrange, pipelines],
  )

  const handleCreatePipeline = useCallback((params?: { id?: string; name?: string; description?: string }) => {
    const name = params?.name || `Pipeline ${Object.keys(pipelines).length + 1}`
    const id = params?.id || (globalThis.crypto?.randomUUID?.() ?? `pipeline-${Date.now()}`)
    const onSuccess = (created: Pipeline) => {
      setPendingPipelines(prev => ({ ...prev, [created.id]: created }))
      setActivePipelineId(created.id)
      loadPipelineToCanvas(created, created.id)
      requestAnimationFrame(() => onArrange?.())
    }
    if (agentId) {
      // An agent owns exactly one pipeline; creating one points the agent at it.
      saveAgentMutation.mutate(
        { id, name, description: params?.description, definition: { nodes: [], edges: [] } },
        { onSuccess },
      )
    } else {
      createMutation.mutate({ id: params?.id, name, description: params?.description }, { onSuccess })
    }
  }, [agentId, createMutation, loadPipelineToCanvas, onArrange, pipelines, saveAgentMutation])

  const handleDeletePipeline = useCallback((pipelineId: string) => {
    if (agentId) {
      toast.error("An agent's pipeline cannot be deleted here")
      return
    }
    if (Object.keys(pipelines).length <= 1) {
      toast.error('Cannot delete the last pipeline')
      return
    }

    deleteMutation.mutate(pipelineId, {
      onSuccess: () => {
        // Load fallback pipeline after deletion
        const remaining = Object.values(pipelines).filter(p => p.id !== pipelineId)
        const fallback = remaining[0] ?? null
        setActivePipelineId(fallback?.id ?? null)
        loadPipelineToCanvas(fallback, fallback?.id)
        requestAnimationFrame(() => onArrange?.())
      },
    })
  }, [agentId, deleteMutation, loadPipelineToCanvas, onArrange, pipelines])


  // Auto-load first pipeline when data arrives. Also self-heals when the
  // active id is no longer in the map (e.g. the pipelines prop swapped scope
  // before/after selection) — without this the selector shows no selection.
  useEffect(() => {
    if (loadingPipelines) return
    if (activePipelineId && pipelines[activePipelineId]) return

    const firstId = Object.keys(pipelines)[0]
    if (firstId) {
      handlePipelineSelect(firstId)
    }
  }, [activePipelineId, handlePipelineSelect, loadingPipelines, pipelines])

  // Clear state when workspace changes (but not on initial mount)
  const prevWorkspaceKeyRef = useRef(`${organizationId}/${workspaceId}`)
  useEffect(() => {
    const key = `${organizationId}/${workspaceId}`
    if (prevWorkspaceKeyRef.current !== key) {
      prevWorkspaceKeyRef.current = key
      setActivePipelineId(null)
      setPendingPipelines({})
      loadPipelineToCanvas(null)
    }
  }, [loadPipelineToCanvas, organizationId, workspaceId])

  return {
    pipelines,
    activePipelineId,
    setActivePipelineId,
    isActiveBinding,
    loadingPipelines,
    isSaving: updateMutation.isPending || saveAgentMutation.isPending,
    isCreating: createMutation.isPending,
    handleSave,
    handleEditPipeline,
    handleTogglePipelineHidden,
    handlePipelineSelect,
    handleClonePipeline,
    handleCreatePipeline,
    handleDeletePipeline,
    loadPipelineToCanvas,
  }
}
